export const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const K = { token: "workorders_token", refresh: "workorders_refresh", media: "workorders_media" };

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => (onUnauthorized = fn);
export const currentToken = () => localStorage.getItem(K.token);
export const currentRefresh = () => localStorage.getItem(K.refresh);

export function saveToken(token, refresh, media) {
  if (!token) return Object.values(K).forEach((k) => localStorage.removeItem(k));
  localStorage.setItem(K.token, token);
  if (refresh) localStorage.setItem(K.refresh, refresh);
  if (media) localStorage.setItem(K.media, media);
}

let refreshing = null;
let deadRefresh = null;
export function refreshSession() {
  const rt = currentRefresh();
  if (!rt || rt === deadRefresh) return Promise.resolve(false);
  refreshing ??= (async () => {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (res.status === 401 || res.status === 422) {
      deadRefresh = rt;
      return false;
    }
    if (!res.ok) throw new Error(`Refresh failed (${res.status})`);
    const d = await res.json();
    saveToken(d.access_token, d.refresh_token, d.media_token);
    return true;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

const authHeader = () => (currentToken() ? { Authorization: `Bearer ${currentToken()}` } : {});

async function errorOf(res) {
  const d = await res.json().catch(() => ({}));
  return new Error(typeof d.detail === "string" ? d.detail : `Request failed (${res.status})`);
}

export async function api(path, opts = {}, retried = false) {
  const body = opts.body !== undefined ? JSON.stringify(opts.body) : undefined;
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? "GET",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body,
    keepalive: !!body && body.length < 60000,
  });
  if (res.status === 401 && currentToken() && !path.startsWith("/auth/login")) {
    if (!retried && (await refreshSession().catch(() => null))) return api(path, opts, true);
    if (!retried) {
      saveToken(null);
      onUnauthorized?.();
    }
  }
  if (!res.ok) throw await errorOf(res);
  return res.json();
}

export const fileUrl = (path) => `${BASE}/files/${path}?token=${localStorage.getItem(K.media) ?? ""}`;
export const logoUrl = (b) => `${BASE}/branding/logo?v=${encodeURIComponent(b?.updated_at ?? "")}`;

const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm", "3gp"];
export const isVideo = (p) => VIDEO_EXTS.includes((p.split("?")[0].split(".").pop() ?? "").toLowerCase());
const isImage = (p) => ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes((p.split(".").pop() ?? "").toLowerCase());
export { isImage };

async function uploadSmall(file) {
  const send = () => {
    const form = new FormData();
    form.append("file", file, file.name);
    return fetch(`${BASE}/upload`, { method: "POST", headers: authHeader(), body: form });
  };
  let res = await send();
  if (res.status === 401 && (await refreshSession().catch(() => false))) res = await send();
  if (!res.ok) throw await errorOf(res);
  return (await res.json()).path;
}

const CHUNK = 3 * 1024 * 1024;
function toBase64(bytes) {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

async function uploadVideo(file, onProgress) {
  const mime = file.type || "video/mp4";
  const ext = mime.includes("quicktime") ? "mov" : (mime.split("/")[1] ?? "mp4").replace("x-", "").slice(0, 5);
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  let path = "";
  for (let i = 0; i < total; i++) {
    const bytes = new Uint8Array(await file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK)).arrayBuffer());
    const r = await api("/upload/chunk", {
      method: "POST",
      body: { upload_id: uploadId, index: i, total, data: toBase64(bytes), ext, content_type: mime },
    });
    onProgress?.(Math.round(((i + 1) / total) * 100));
    if (r.path) path = r.path;
  }
  if (!path) throw new Error("Upload failed");
  return path;
}

/** Upload any File (photo, video, PDF…) and return its storage path. */
export const uploadFile = (file, onProgress) =>
  file.type.startsWith("video/") ? uploadVideo(file, onProgress) : uploadSmall(file);

export const money = (n) =>
  `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const num = (n) => (n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

export const ROLE_LABELS = { admin: "Admin", supervisor: "Supervisor", technician: "Technician" };
export const EMPLOYEE_STATUSES = [
  { value: "active", label: "Active" },
  { value: "quit", label: "Quit" },
  { value: "laid_off", label: "Laid off" },
  { value: "fired", label: "Fired" },
];

export const progressOf = (e) => e.progress ?? (e.completed ? 1 : 0);
export function quarters(n) {
  const whole = Math.floor(n + 1e-9);
  const frac = ["", "¼", "½", "¾"][Math.round((n - whole) * 4) % 4];
  return whole ? `${whole}${frac}` : frac || "0";
}
export const budgetTone = (pct) => (pct <= 50 ? "ok" : pct <= 60 ? "warn" : "over");
export function budgetLevel(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct >= 100) return "over";
  if (pct >= 80) return "warn";
  return null;
}

export function computeTotals(wo, month) {
  const inMonth = (e) => !month || (e.date ?? "").startsWith(month);
  const hours = (wo.entries.hours ?? []).filter(inMonth);
  const exps = (wo.entries.expenses ?? []).filter(inMonth);
  const sum = (arr, f) => arr.reduce((a, x) => a + (f(x) || 0), 0);
  const total_hours = sum(hours, (h) => h.hours);
  const man_hours = sum(hours, (h) => (h.hours || 0) * (h.num_techs || 0));
  const labor_cost = sum(hours, (h) => h.labor_cost);
  const total_expenses = sum(exps, (e) => e.cost);
  const total_per_diems = sum(hours, (h) => h.per_diem_cost);
  const spent = labor_cost + total_expenses + total_per_diems;
  return {
    total_hours,
    man_hours,
    labor_cost,
    total_expenses,
    total_per_diems,
    billable_total: wo.billing_type === "hourly" ? man_hours * (wo.hourly_rate || 0) : 0,
    total_cost: spent,
  };
}

export function emailResultMessage(r) {
  const what =
    r.kind === "to be invoiced" ? "Invoice summary"
      : r.kind === "email for approval" ? "Approval email"
        : r.kind === "send monthly report" ? "Monthly report" : "Kastle email";
  return r.ok ? `${what}${r.month ? ` (${r.month})` : ""} sent to ${r.to}` : `${what} not sent: ${r.error}`;
}

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const todayISO = () => iso(new Date());
export const currentMonth = () => todayISO().slice(0, 7);
export function shiftMonth(m, by) {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
export function monthLabel(m) {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}
export function weekStart(offset) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
  return iso(d);
}

/** POST for a signed link (PDF report or photo share) and open/copy it. */
export async function openLink(path) {
  const { url } = await api(path, { method: "POST" });
  window.open(url, "_blank");
}
export async function copyLink(path) {
  const { url } = await api(path, { method: "POST" });
  await navigator.clipboard?.writeText(url).catch(() => {});
  return url;
}
